import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import HeaderLogo from 'components/commercetools-ui/organisms/header/header-logo';
import HeaderNavigationDesktop from 'components/commercetools-ui/organisms/header/header-navigation/header-navigation-desktop';
import HeaderNavigationMobile from 'components/commercetools-ui/organisms/header/header-navigation/header-navigation-mobile';
import { HeaderProps } from 'components/commercetools-ui/organisms/header/types';
import UtilitySection from 'components/commercetools-ui/organisms/header/utility-section';
import { XMarkIcon as CloseIcon, XMarkIcon, MagnifyingGlassIcon, MapPinIcon, DocumentIcon, PhoneIcon } from '@heroicons/react/24/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useFormat } from 'helpers/hooks/useFormat';
import { useParams, usePathname } from 'next/navigation';
import Link from 'components/commercetools-ui/atoms/link';

const AlgoliaSearch = dynamic(() => import('components/commercetools-ui/atoms/search-algolia'));

const Header: React.FC<HeaderProps> = ({
  navLinks,
  data,
  topRightMenu,
  topCentreMenu,
  categories,
  logo,
  logoMobile,
  logoLink,
  logoLinkMobile,
  tiles,
  emptyCartTitle,
  emptyCartSubtitle,
  emptyCartImage,
  emptyCartCategories,
  emptyWishlistTitle,
  emptyWishlistSubtitle,
  emptyWishlistImage,
  emptyWishlistCategories,
  enableAlgoliaSearch = false,
}) => {
  const [showSearchBar, setShowSearchBar] = useState(false);
  const { formatMessage: formatCommonMessage } = useFormat({ name: 'common' });
  const currentURL = usePathname();
  const { locale } = useParams();

  const isSearchPage = currentURL.includes('/search/');
  return (
    <header className="relative h-fit w-full border-b border-neutral-400 bg-white">

      <div
        aria-label="Top"
        className="flex w-full items-center justify-between px-[20.5px] md:px-24 md:pt-0 lg:px-48 xl:px-60 2xl:px-80 2xl:py-8"
      >
        {/* <HeaderNavigationMobile logo={logoMobile} links={navLinks} logoLink={logoLinkMobile} /> */}

        <div className="ml-0 hidden w-full justify-start lg:flex lg:w-fit">
          <HeaderLogo
            logo={logo}
            logoLink={logoLink}
            imageClassName="flex h-58 3xl:h-68 w-188 text-16 font-bold md:w-[18rem] 3xl:w-[27.125rem]"
          />
        </div>
        <div className="ml-0 flex w-full justify-start lg:hidden">
          <HeaderLogo
            logo={logoMobile}
            logoLink={logoLinkMobile}
            imageClassName="flex h-68 text-16 font-bold w-[8.25rem] 2xl:w-[27.125rem]"
          />
        </div>
        {!isSearchPage && (
          <div
            className="ml-auto hover:cursor-pointer lg:hidden xl:pr-24"
            onClick={() => setShowSearchBar(!showSearchBar)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
              <path
                d="M22.75 22.75L16.25 16.25M18.4167 10.8333C18.4167 15.0215 15.0215 18.4167 10.8333 18.4167C6.64517 18.4167 3.25 15.0215 3.25 10.8333C3.25 6.64517 6.64517 3.25 10.8333 3.25C15.0215 3.25 18.4167 6.64517 18.4167 10.8333Z"
                stroke="#666666"
                strokeWidth="2"
                strokeLinecap="square"
                strokeLinejoin="round"
              />
            </svg>
            {/* <Search categories={categories} /> */}
          </div>
        )}
        <div className="pr-24 lg:hidden">
          <UtilitySection
            emptyCartTitle={emptyCartTitle}
            emptyCartSubtitle={emptyCartSubtitle}
            emptyCartImage={emptyCartImage}
            emptyCartCategories={emptyCartCategories}
            emptyWishlistTitle={emptyWishlistTitle}
            emptyWishlistSubtitle={emptyWishlistSubtitle}
            emptyWishlistImage={emptyWishlistImage}
            emptyWishlistCategories={emptyWishlistCategories}
          />
        </div>


        {(topCentreMenu || topRightMenu) && (
          <div className="hidden lg:flex gap-10"> {/* Parent container with flex */}

          {topCentreMenu && (
                <div>
                  <div className="flex gap-18">
                    {topCentreMenu.map((link: any) => (
                      <Link key={link.menu_links.title} link={link.menu_links.href} rel="noopener noreferrer">
                        <span className="flex text-12 text-black hover:cursor-pointer hover:text-[#A9A9A9] 2xl:text-14">
                         {link.menu_links.title === 'Find a stockist' ? (
                           <MapPinIcon className="ml-3 mt-1 w-16 text-black" />
                         ) : link.menu_links.title === 'Register a product' ? (
                           <DocumentIcon className="ml-3 mt-1 w-16 text-black" />
                          ) : (link.menu_links.title === '13 14 16' || link.menu_links.title === '0800 804 222') ? (
                            <PhoneIcon className="ml-3 mt-1 w-16 text-black" />
                         ): null}
                         <span className="ml-2 text-black">{link.menu_links.title}</span>
                       </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {topRightMenu && (
                <div className="ml-100"> {/* Add left margin to create spacing */}
                  <div className="flex gap-18">
                    {topRightMenu.map((link: any) => (
                      <Link key={link.menu_links.title} link={link.menu_links.href} rel="noopener noreferrer" target="_blank">
                        <span className="flex text-12 font-semibold text-[#666] hover:cursor-pointer hover:text-[#333] 2xl:text-14">
                          {link.menu_links.title}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            
          </div>
        )}


        <HeaderNavigationMobile
          logo={logoMobile}
          links={navLinks}
          topRightMenu={topRightMenu}
          topCentreMenu={topCentreMenu}
          logoLink={logoLinkMobile}
          showSearchBar={showSearchBar}
          setShowSearchBar={setShowSearchBar}
        />
      </div>

      {/* <div className="relative block w-full border-t border-neutral-400 px-16 py-8 md:px-24 lg:px-20 lg:hidden lg:px-48">
        <Search categories={categories} />
      </div> */}
      <div className="flex justify-between">
        <HeaderNavigationDesktop
          links={navLinks}
          tiles={tiles ?? []}
          showSearchBar={showSearchBar}
          categories={categories}
          setShowSearchBar={setShowSearchBar}
        />
        <div className="hidden lg:block">
          <div className="flex items-center px-48 xl:pr-60 2xl:pr-80">
            <div
              className="-mt-2 ml-auto py-10 hover:cursor-pointer 3xl:-mt-0"
              onClick={() => setShowSearchBar(!showSearchBar)}
            >
              {!isSearchPage && (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 26 26" fill="none">
                  <path
                    d="M22.75 22.75L16.25 16.25M18.4167 10.8333C18.4167 15.0215 15.0215 18.4167 10.8333 18.4167C6.64517 18.4167 3.25 15.0215 3.25 10.8333C3.25 6.64517 6.64517 3.25 10.8333 3.25C15.0215 3.25 18.4167 6.64517 18.4167 10.8333Z"
                    stroke="#666666"
                    strokeWidth="2"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {/* <Search categories={categories} /> */}
            </div>

            {/* <UtilitySection
              emptyCartTitle={emptyCartTitle}
              emptyCartSubtitle={emptyCartSubtitle}
              emptyCartImage={emptyCartImage}
              emptyCartCategories={emptyCartCategories}
              emptyWishlistTitle={emptyWishlistTitle}
              emptyWishlistSubtitle={emptyWishlistSubtitle}
              emptyWishlistImage={emptyWishlistImage}
              emptyWishlistCategories={emptyWishlistCategories}
            /> */}
          </div>
        </div>
      </div>

      <div className={`${showSearchBar ? 'block' : 'hidden'} relative z-[1000] bg-white md:px-24 md:py-12 lg:hidden`}>
        <AlgoliaSearch categories={categories} showSearchBar={showSearchBar} setShowSearchBar={setShowSearchBar} />
        <XMarkIcon
          className="absolute right-[1.6rem] top-1/2 z-[999] !h-24 !w-24 -translate-y-1/2 stroke-2 text-primary-black hover:cursor-pointer lg:right-[4.5rem]"
          onClick={() => setShowSearchBar(false)}
        />
        <MagnifyingGlassIcon
          className="stroke-10 absolute left-[2rem] top-1/2 z-[999] h-24 w-24 -translate-y-1/2 text-[#666] lg:hidden"
          onClick={() => setShowSearchBar(false)}
        />
      </div>
    </header>
  );
};
export default Header;
